/**
 * RecentOrgsBlock: `recent-orgs` DYNAMIC content block.
 *
 * data: { limit? }
 *
 * Fetches the most recently approved organizations sorted by createdAt desc
 * and delegates rendering to the shared OrgCarousel component. The section
 * stays fresh automatically as new orgs are approved — no curation required.
 *
 * The `limit` config (default 9) controls the total number of orgs fetched;
 * the carousel pages through them 3 at a time.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Container, Spinner } from 'react-bootstrap';

import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import OrgCarousel from './OrgCarousel';
import useOrgFetcher from './useOrgFetcher';

export default function RecentOrgsBlock({ data }) {
    const { limit = 9 } = data || {};
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'the ecosystem';

    const orgApi = useOrganizationApi();
    const { organizations, loading } = useOrgFetcher(
        () =>
            orgApi.fetchOrganizations({
                sortBy: 'createdAt',
                sortOrder: 'desc',
                limit,
            }),
        limit
    );

    if (loading) {
        return (
            <section className="recent-orgs-block py-5">
                <Container className="text-center">
                    <Spinner animation="border" variant="primary" size="sm" />
                </Container>
            </section>
        );
    }

    if (organizations.length === 0) {
        return null;
    }

    return (
        <OrgCarousel
            key={limit}
            organizations={organizations}
            heading="Recently Added Organizations"
            subtitle={`New members of ${ecosystemName}`}
            sectionClassName="recent-orgs-block"
        />
    );
}

RecentOrgsBlock.propTypes = {
    data: PropTypes.shape({
        limit: PropTypes.number,
    }),
};

RecentOrgsBlock.defaultProps = {
    data: {},
};
